import { getAnswerGeneration } from '../api/auto-apply-answers';

const DEFAULT_INTERVAL_MS = 2000;
// EN-1116: GenerateApplicationAnswersJob normally settles in under 20s, but a stalled form parse can
// hold it for up to its 360s timeout. After this long the seeker may try again; the POST re-attaches
// to the in-flight generation without spending another credit.
const DEFAULT_TIMEOUT_MS = 180000;
const TIMED_OUT_MESSAGE = 'This is taking longer than usual. Please try AutoApply again in a minute.';
// A dropped connection or a proxy 5xx on one poll says nothing about the generation, which is still
// running on the queue; only this many in a row are treated as a real failure.
const MAX_CONSECUTIVE_TRANSIENT_FAILURES = 3;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * The old synchronous response for a settled generation, or null while it is still running.
 */
const settledResponse = (generation) => {
	if (generation?.status === 'completed') {
		return { status: 200, data: { data: generation.answers } };
	}

	if (generation?.status === 'failed') {
		return { status: generation.error?.status ?? 500, data: { message: generation.error?.message } };
	}

	return null;
};

/**
 * EN-1116: POST /api/auto-apply/answers/generate queues the work and answers 202 with a generation
 * uuid. This polls GET /api/auto-apply/answers/generations/{uuid} until the generation settles and
 * resolves with a response shaped like the old synchronous one ({ status, data }), so callers keep
 * their status handling. Resolves null once `isCancelled()` is true (the AutoApply flow moved on).
 */
const pollAnswerGeneration = async (generationUuid, {
	intervalMs = DEFAULT_INTERVAL_MS,
	timeoutMs = DEFAULT_TIMEOUT_MS,
	isCancelled = () => false,
} = {}) => {
	const deadline = Date.now() + timeoutMs;
	let transientFailures = 0;

	while (Date.now() < deadline) {
		if (isCancelled()) {
			return null;
		}

		let response;
		try {
			response = await getAnswerGeneration(generationUuid);
		} catch (e) {
			transientFailures += 1;
			if (transientFailures >= MAX_CONSECUTIVE_TRANSIENT_FAILURES) {
				throw e;
			}
			await delay(intervalMs);
			continue;
		}

		if (response.status >= 500) {
			transientFailures += 1;
			if (transientFailures >= MAX_CONSECUTIVE_TRANSIENT_FAILURES) {
				return response;
			}
			await delay(intervalMs);
			continue;
		}

		if (response.status >= 400) {
			return response;
		}

		transientFailures = 0;
		const settled = settledResponse(response.data?.data);

		if (settled) {
			return settled;
		}

		await delay(intervalMs);
	}

	return { status: 408, data: { message: TIMED_OUT_MESSAGE } };
};

export {
	pollAnswerGeneration,
};
